import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  FiMonitor,
  FiUser,
  FiUsers,
  FiSmartphone,
  FiLayout,
  FiArrowDown,
  FiStar,
  FiPrinter,
  FiInstagram,
  FiTrello,
  FiTarget,
} from "react-icons/fi";
import Header from "../Header";
import FooterWithMenu from "../Footer";
import blogPosts from "../../data/blogPosts";

const iconMap = {
  desktop: FiMonitor,
  user: FiUser,
  users: FiUsers,
  phone: FiSmartphone,
  layout: FiLayout,
  print: FiPrinter,
  instagram: FiInstagram,
  browser: FiTrello,
  ads: FiTarget,
};

const ChallengeIcon = ({ name }) => {
  const Icon = iconMap[name] || FiLayout;
  return <Icon className="w-7 h-7 text-gray-700" />;
};

const resolveImage = (value, fallbackAlt = "Image") => {
  if (!value) return null;
  if (typeof value === "string") return { src: value, alt: fallbackAlt };
  if (typeof value === "object" && value.src) {
    return { src: value.src, alt: value.alt || fallbackAlt };
  }
  return null;
};

const BlogDetail = () => {
  const { slug } = useParams();
  const post = blogPosts.find((item) => item.slug === slug);
  const [activeSection, setActiveSection] = useState(null);
  const sectionsRef = useRef({});

  useEffect(() => {
    if (!post?.contents) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    post.contents.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        sectionsRef.current[id] = element;
        observer.observe(element);
      }
    });
    return () => observer.disconnect();
  }, [post]);

  if (!post) return <Navigate to="/case-study" replace />;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const heroImage = resolveImage(
    post.heroImage || post.heroMockup,
    `${post.title} mockup`
  );

  const heroSectionStyle = post.heroBackground
    ? { background: post.heroBackground }
    : { backgroundImage: "url('/assets/svg/plain_dot.svg')" };

  return (
    <>
      <Header />

      {/* Full-width hero section */}
      <section
        className="relative w-full overflow-hidden"
        style={heroSectionStyle}
      >
        <div className="container mx-auto px-4 md:px-12 lg:px-20 pt-8 pb-12">
          <div className="flex flex-wrap gap-2 md:gap-3 mb-6">
            {post.client && (
              <span className="inline-block border border-gray-300 bg-white/60 rounded-full px-4 py-1 text-sm font-inter text-gray-700">
                Client : <span className="font-semibold">{post.client}</span>
              </span>
            )}
            {post.service && (
              <span className="inline-block border border-gray-300 bg-white/60 rounded-full px-4 py-1 text-sm font-inter text-gray-700">
                Service : <span className="font-semibold">{post.service}</span>
              </span>
            )}
            {post.duration && (
              <span className="inline-block border border-gray-300 bg-white/60 rounded-full px-4 py-1 text-sm font-inter text-gray-700">
                Duration : <span className="font-semibold">{post.duration}</span>
              </span>
            )}
            {post.deliverables && (
              <span className="inline-block border border-gray-300 bg-white/60 rounded-full px-4 py-1 text-sm font-inter text-gray-700">
                Deliverables : <span className="font-semibold">{post.deliverables}</span>
              </span>
            )}
            {post.tools && (
              <span className="inline-block border border-gray-300 bg-white/60 rounded-full px-4 py-1 text-sm font-inter text-gray-700">
                Tools : <span className="font-semibold">{post.tools}</span>
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-medium text-gray-900 font-inter leading-tight mb-6 max-w-md">
                {post.title}
              </h1>
              <p className="text-base md:text-lg text-gray-700 font-poppins leading-7 mb-8 max-w-xl">
                {post.excerpt}
              </p>
              {post.contents?.[0] && (
                <button
                  onClick={() => scrollToSection(post.contents[0].id)}
                  className="inline-flex items-center gap-3 text-gray-900 font-inter font-semibold text-sm uppercase tracking-wide hover:text-[#F15A28] transition-colors"
                >
                  <span className="w-12 h-px bg-gray-900"></span>
                  <span>SCROLL TO EXPLORE</span>
                  <FiArrowDown className="w-4 h-4" />
                </button>
              )}
            </div>

            {heroImage && (
              <div className="relative">
                <img
                  src={heroImage.src}
                  alt={heroImage.alt}
                  className="w-full h-auto object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-20 pt-10 pb-16">
        <div className="flex flex-col lg:flex-row gap-10">
          {post.contents && post.contents.length > 0 && (
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-28">
                <h4 className="text-sm font-inter font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  Contents
                </h4>
                <nav className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible">
                  {post.contents.map(({ id, label }) => (
                    <button
                      key={id}
                      onClick={() => scrollToSection(id)}
                      className={`text-left text-sm font-poppins px-3 py-2 rounded-lg whitespace-nowrap transition-colors ${
                        activeSection === id
                          ? "bg-[#F15A28]/10 text-[#F15A28] font-semibold"
                          : "text-gray-600 hover:text-[#F15A28]"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>
          )}

          <main className="flex-1 min-w-0">
            {post.overview && (
              <section id="overview" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl md:text-4xl font-medium text-gray-900 font-inter mb-6">
                  {post.overview.heading || "Overview"}
                </h2>
                {resolveImage(post.overview.image, "Overview") ? (
                  <div className="rounded-2xl overflow-hidden">
                    <img
                      src={resolveImage(post.overview.image, "Overview").src}
                      alt={resolveImage(post.overview.image, "Overview").alt}
                      className="w-full h-auto object-contain"
                    />
                  </div>
                ) : (
                  <div className="bg-gradient-to-br from-[#7C5CFC]/10 via-[#F15A28]/5 to-purple-100 rounded-3xl p-6 md:p-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                      {post.overview.cards?.map((card, index) => {
                        const image = resolveImage(
                          card.image,
                          card.caption || "Overview image"
                        );
                        return (
                          <div
                            key={index}
                            className="bg-white rounded-2xl overflow-hidden shadow-sm"
                          >
                            {image && (
                              <img
                                src={image.src}
                                alt={image.alt}
                                className="w-full h-40 object-cover"
                              />
                            )}
                            {card.caption && (
                              <p className="px-4 py-3 text-sm text-gray-700 font-poppins">
                                {card.caption}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            )}

            {post.aboutClient && (
              <section id="about-client" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl md:text-4xl font-medium text-gray-900 font-inter mb-4">
                  About the Client
                </h2>
                <p className="text-base md:text-lg text-gray-700 font-poppins leading-8 max-w-3xl">
                  {post.aboutClient}
                </p>
              </section>
            )}

            {post.requirement && (
              <section id="the-requirement" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl md:text-4xl font-medium text-gray-900 font-inter mb-4">
                  The Requirement
                </h2>
                {post.requirement.intro && (
                  <p className="text-base md:text-lg text-gray-700 font-poppins leading-8 mb-6 max-w-3xl">
                    {post.requirement.intro}
                  </p>
                )}
                {post.requirement.deliverables &&
                  post.requirement.deliverables.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                      {post.requirement.deliverables.map((card, index) => (
                        <div
                          key={index}
                          className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col items-center text-center gap-3 hover:shadow-md transition-shadow"
                        >
                          <ChallengeIcon name={card.icon} />
                          <p className="text-sm font-inter font-semibold text-gray-800 leading-tight">
                            {card.title}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
              </section>
            )}

            {post.challenge && (
              <section id="the-challenge" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl md:text-4xl font-medium text-gray-900 font-inter mb-4">
                  The Challenge
                </h2>
                {post.challenge.intro && (
                  <p className="text-base md:text-lg text-gray-700 font-poppins leading-8 mb-6 max-w-3xl">
                    {post.challenge.intro}
                  </p>
                )}

                {resolveImage(
                  post.challenge.screenshot,
                  "Current state screenshot"
                ) && (
                  <div className="relative rounded-2xl overflow-hidden mb-6">
                    <img
                      src={
                        resolveImage(
                          post.challenge.screenshot,
                          "Current state screenshot"
                        ).src
                      }
                      alt={
                        resolveImage(
                          post.challenge.screenshot,
                          "Current state screenshot"
                        ).alt
                      }
                      className="w-full h-64 md:h-96 object-cover"
                    />
                    {post.challenge.mainCard && (
                      <div className="absolute bottom-4 right-4 bg-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-3">
                        <ChallengeIcon name={post.challenge.mainCard.icon} />
                        <p className="text-sm font-inter font-semibold text-gray-800">
                          {post.challenge.mainCard.title}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {post.challenge.highlightCards &&
                  post.challenge.highlightCards.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      {post.challenge.highlightCards.map((card, index) => {
                        const image = resolveImage(card.image, card.title);
                        return (
                          <div
                            key={index}
                            className="relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-stretch">
                              <div className="p-5 md:p-6 flex-1">
                                <h3 className="text-lg md:text-xl font-inter font-semibold text-gray-900 mb-2">
                                  {card.title}
                                </h3>
                                <p className="text-sm md:text-base text-gray-700 font-poppins leading-6">
                                  {card.description}
                                </p>
                              </div>
                              {image && (
                                <div className="w-1/2 max-w-[50%] flex-shrink-0">
                                  <img
                                    src={image.src}
                                    alt={image.alt}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                {post.challenge.issueCards &&
                  post.challenge.issueCards.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                      {post.challenge.issueCards.map((card, index) => (
                        <div
                          key={index}
                          className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-start gap-3 hover:shadow-md transition-shadow"
                        >
                          <ChallengeIcon name={card.icon} />
                          <p className="text-sm font-inter font-semibold text-gray-800 leading-tight">
                            {card.title}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
              </section>
            )}

            {post.approach && post.approach.length > 0 && (
              <section id="our-approach" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl md:text-4xl font-medium text-gray-900 font-inter mb-4">
                  Our Approach
                </h2>
                <p className="text-base md:text-lg text-gray-700 font-poppins leading-8 mb-8 max-w-3xl">
                  We followed a structured process, ensuring every decision was
                  driven by data and user needs.
                </p>
                <ol className="space-y-3 max-w-2xl relative">
                  {post.approach.map((step, index) => (
                    <li
                      key={index}
                      className="flex items-center gap-4 bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm"
                    >
                      <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[#F15A28] text-white font-inter font-semibold text-sm flex items-center justify-center">
                        {index + 1}
                      </span>
                      <span className="text-base font-poppins text-gray-800">
                        {step}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {(post.featureBlocks?.length > 0 ||
              post.designApproach ||
              post.designExtra) && (
              <section id="design-approach" className="mb-16 scroll-mt-24">
                {post.designApproach && (
                  <>
                    <h2 className="text-3xl md:text-4xl font-medium text-gray-900 font-inter mb-4">
                      Design Approach
                    </h2>
                    <p className="text-base md:text-lg text-gray-700 font-poppins leading-8 mb-8 max-w-3xl">
                      {post.designApproach}
                    </p>
                  </>
                )}

                {post.featureBlocks && post.featureBlocks.length > 0 && (
                  <div
                    id="visual-design"
                    className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 scroll-mt-24"
                  >
                    {post.featureBlocks.map((block, index) => {
                      const image = resolveImage(block.image, block.title || block.label);
                      const captionLabel = block.label || (!block.description && block.title);
                      const isImageOnly = !block.description;
                      return (
                        <div key={index}>
                          {image && (
                            <div className="relative rounded-2xl overflow-hidden mb-4">
                              <img
                                src={image.src}
                                alt={image.alt}
                                className={
                                  isImageOnly
                                    ? "w-full h-auto object-contain"
                                    : "w-full h-72 object-cover"
                                }
                              />
                              {captionLabel && (
                                <span className="absolute bottom-3 right-3 bg-[#F15A28] text-white text-xs font-inter font-semibold px-3 py-1 rounded-md">
                                  {captionLabel}
                                </span>
                              )}
                            </div>
                          )}
                          {!isImageOnly && (
                            <>
                              <h3 className="text-xl md:text-2xl font-medium text-gray-900 font-inter mb-2">
                                {block.title}
                              </h3>
                              <p className="text-sm md:text-base text-gray-700 font-poppins leading-7">
                                {block.description}
                              </p>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {post.designExtra &&
                  resolveImage(post.designExtra.image, post.designExtra.title || "Design showcase") && (
                    <div className="mt-8 md:mt-10">
                      <div className="rounded-2xl overflow-hidden mb-4">
                        <img
                          src={
                            resolveImage(
                              post.designExtra.image,
                              post.designExtra.title || "Design showcase"
                            ).src
                          }
                          alt={
                            resolveImage(
                              post.designExtra.image,
                              post.designExtra.title || "Design showcase"
                            ).alt
                          }
                          className="w-full h-auto object-contain"
                        />
                      </div>
                      {post.designExtra.title && (
                        <h3 className="text-xl md:text-2xl font-medium text-gray-900 font-inter mb-2">
                          {post.designExtra.title}
                        </h3>
                      )}
                      {post.designExtra.description && (
                        <p className="text-sm md:text-base text-gray-700 font-poppins leading-7 max-w-3xl">
                          {post.designExtra.description}
                        </p>
                      )}
                    </div>
                  )}
              </section>
            )}

            {post.revision && (
              <section id="revisions" className="mb-16 scroll-mt-24">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center">
                  {resolveImage(
                    post.revision.image,
                    post.revision.title || "Revision"
                  ) && (
                    <div className="rounded-2xl overflow-hidden bg-gray-50">
                      <img
                        src={
                          resolveImage(
                            post.revision.image,
                            post.revision.title || "Revision"
                          ).src
                        }
                        alt={
                          resolveImage(
                            post.revision.image,
                            post.revision.title || "Revision"
                          ).alt
                        }
                        className="w-full h-64 md:h-80 object-cover"
                      />
                    </div>
                  )}
                  <div>
                    <h2 className="text-3xl md:text-4xl font-medium text-gray-900 font-inter mb-4">
                      {post.revision.title || "Revision & Feedback"}
                    </h2>
                    <p className="text-base md:text-lg text-gray-700 font-poppins leading-8">
                      {post.revision.description}
                    </p>
                  </div>
                </div>
              </section>
            )}

            {post.outcome && (
              <section id="final-outcome" className="mb-16 scroll-mt-24">
                <h2 className="text-3xl md:text-4xl font-medium text-gray-900 font-inter mb-4">
                  Final Outcome
                </h2>
                {post.outcome.description && (
                  <p className="text-base md:text-lg text-gray-700 font-poppins leading-8 mb-6">
                    {post.outcome.description}
                  </p>
                )}
                {post.outcome.images && post.outcome.images.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
                    {post.outcome.images.map((item, index) => {
                      const image = resolveImage(item, `Outcome ${index + 1}`);
                      if (!image) return null;
                      const isLastOdd =
                        post.outcome.images.length % 2 === 1 &&
                        index === post.outcome.images.length - 1;
                      return (
                        <div
                          key={index}
                          className={`rounded-2xl overflow-hidden ${
                            isLastOdd ? "md:col-span-2" : ""
                          }`}
                        >
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="w-full h-auto object-contain"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {post.testimonial && (
              <section className="mb-8">
                <h3 className="text-2xl md:text-3xl font-medium text-gray-900 font-inter mb-3">
                  Client Testimonial
                </h3>
                <p className="text-base text-gray-600 font-poppins mb-5">
                  A seamless collaboration that resulted in:
                </p>
                <div className="bg-[#FFF4D6] border border-[#F5DC8A] rounded-2xl p-6 md:p-10">
                  {post.testimonial.headline && (
                    <p className="text-lg md:text-xl font-inter font-semibold text-gray-900 mb-4">
                      {post.testimonial.headline}
                    </p>
                  )}
                  <p className="text-base md:text-lg text-gray-700 font-poppins leading-8 mb-6">
                    "{post.testimonial.quote}"
                  </p>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="text-base font-inter font-semibold text-gray-900">
                        {post.testimonial.name}
                      </p>
                      {post.testimonial.role && (
                        <p className="text-sm text-gray-600 font-poppins">
                          {post.testimonial.role}
                        </p>
                      )}
                      {post.testimonial.rating && (
                        <div className="flex gap-1 mt-2">
                          {Array.from({
                            length: post.testimonial.rating,
                          }).map((_, index) => (
                            <FiStar
                              key={index}
                              className="w-4 h-4 fill-[#F15A28] text-[#F15A28]"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {resolveImage(
                      post.testimonial.logo,
                      `${post.client} logo`
                    ) && (
                      <img
                        src={resolveImage(post.testimonial.logo, `${post.client} logo`).src}
                        alt={resolveImage(post.testimonial.logo, `${post.client} logo`).alt}
                        className="h-14 w-14 object-contain rounded-full bg-white p-1 border"
                      />
                    )}
                  </div>
                </div>
              </section>
            )}

            <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-12 mt-12 text-center">
              <h3 className="text-2xl md:text-3xl font-medium font-inter mb-3">
                Have a project in mind?
              </h3>
              <p className="text-gray-300 font-poppins mb-6 max-w-xl mx-auto">
                Let's talk about how we can bring your ideas to life - premium
                design, tailored to your vision.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-[#F15A28] hover:bg-[#d94a1c] text-white font-semibold px-8 py-3 rounded-full font-inter transition-colors"
              >
                Let's Talk
              </Link>
            </div>
          </main>
        </div>
      </div>

      <FooterWithMenu />
    </>
  );
};

export default BlogDetail;
